import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FiltersBar } from "@/components/FiltersBar";
import { FlowerHeatmap } from "@/components/FlowerHeatmap";
import { ScoreBreakdown } from "@/components/ScoreBreakdown";
import { HeatLegend } from "@/components/HeatLegend";
import { PrintButton, AreaInsights } from "@/components/IndividualReportView";
import { OverallInsight } from "@/components/OverallInsight";
import { useInsights } from "@/lib/useInsights";
import { api, errMsg, paramsFrom } from "@/lib/api";
import { AREAS, total, fmtDate, fmtScore, band, average } from "@/lib/heat";
import { AreaTimeline } from "@/components/AreaTimeline";
import { DemographicSection } from "@/components/DemographicSection";

function filterSummary(sp) {
  const p = Object.fromEntries(sp.entries());
  const parts = [];
  const orgs = sp.getAll("organization");
  if (orgs.length) parts.push(orgs.join(" + "));
  const places = [...sp.getAll("city"), ...sp.getAll("state")];
  if (places.length) parts.push(places.join(", "));
  const demo = [...sp.getAll("event_group"), ...sp.getAll("gender"), ...sp.getAll("race")];
  if (demo.length) parts.push(demo.join(", "));
  parts.push({ group: "Group / Team entries", everyone: "Individual + Group / Team entries" }[p.entry_type] || "Individual entries");
  if (p.age_min || p.age_max) parts.push(`Age ${p.age_min || "0"}–${p.age_max || "+"}`);
  if (p.start || p.end) parts.push(`${p.start ? fmtDate(p.start) : "…"} – ${p.end ? fmtDate(p.end) : "today"}`);
  if (p.q) parts.push(`“${p.q}”`);
  if (p.include_sample === "false") parts.push("Live entries only");
  return parts.join(" · ");
}

const TeamSection = ({ entries }) => {
  if (!entries?.length) return null;
  return (
    <section className="page-break mt-12" data-testid="group-team-entries">
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">Group / Team assessments</h2>
      <p className="mt-1 text-sm text-slate-500">
        {entries.length} assessment{entries.length === 1 ? "" : "s"} completed together as a group. These are shown separately and are not counted in the group score above.
      </p>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {entries.map((m) => <MemberCard key={m.id} m={m} />)}
      </div>
    </section>
  );
};

const MemberCard = ({ m }) => (
  <Link
    to={`/reports/individual/${m.id}`}
    data-testid={`group-member-${m.id}`}
    className="avoid-break block rounded-xl border border-slate-200 bg-white p-3 hover:border-[#1F4266]/40 hover:-translate-y-0.5 transition-[transform,border-color]"
  >
    <div className="flex justify-center">
      <FlowerHeatmap scores={m.scores} size={140} labels={false} testId={`member-heatmap-${m.id}`} />
    </div>
    <div className="mt-2 font-display font-semibold text-slate-900 truncate">{m.name}</div>
    <div className="text-[11px] text-slate-500 truncate">{m.organization || "—"} · {fmtDate(m.submitted_at)}</div>
    <div className="mt-2 grid grid-cols-5 gap-1 text-center">
      {AREAS.map((a) => (
        <div key={a.key}>
          <div className="text-[9px] uppercase tracking-wide text-slate-400">{a.label.slice(0, 3)}</div>
          <div className="font-mono text-xs text-slate-800">{fmtScore(m.scores[a.key])}</div>
        </div>
      ))}
    </div>
    <div className="mt-1.5 text-right font-mono text-xs font-semibold text-[#1F4266]">Total {fmtScore(total(m.scores))}</div>
  </Link>
);

export default function GroupReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const insights = useInsights();
  const qs = searchParams.toString();
  const params = paramsFrom(new URLSearchParams(qs));

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/reports/group", { params: paramsFrom(new URLSearchParams(qs)) })
        .then((r) => setData(r.data))
        .catch((e) => toast.error(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [qs]);

  return (
    <div className="space-y-8">
      <FiltersBar searchParams={searchParams} setSearchParams={setSearchParams} groupMode />
      {!data ? (
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#1F4266]" />
      ) : (
        <article data-testid="group-report" className="print-plain rounded-2xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
          <header className="flex flex-wrap items-start justify-between gap-6 border-b border-slate-200 pb-6">
            <div className="flex items-center gap-4">
              <img src="/flower-logo.png" alt="" className="h-12 w-12" />
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Group Assessment Report</div>
                <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1F4266]" data-testid="group-filter-summary">
                  {filterSummary(params)}
                </h1>
              </div>
            </div>
            <PrintButton testId="print-group-report-button" />
          </header>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Participants" value={data.count} testId="group-count" />
            <Stat label="Average total" value={`${fmtScore(total(data.averages))} / ${data.max_score * 5}`} testId="group-avg-total" />
            <Stat label="First submission" value={fmtDate(data.first_date)} />
            <Stat label="Latest submission" value={fmtDate(data.last_date)} />
          </div>
          {data.count === 0 ? (
            <div data-testid="group-empty-wrap"><p className="py-16 text-center text-slate-500" data-testid="group-empty">No individual entries match these filters.</p><TeamSection entries={data.team_entries} /></div>
          ) : (
            <>
              <section className="mt-8 grid gap-10 lg:grid-cols-[1.1fr_1fr] items-center">
                <div className="flex justify-center avoid-break fade-up">
                  <FlowerHeatmap scores={data.averages} max={data.max_score} size={440} testId="group-heatmap-canvas" />
                </div>
                <div className="fade-up-2">
                  <div className="mb-4 text-xs font-mono uppercase tracking-widest text-slate-500">Average per area · range (min–max)</div>
                  <ScoreBreakdown
                    scores={data.averages}
                    max={data.max_score}
                    extra={(k) => `range ${fmtScore(data.stats[k].min)}–${fmtScore(data.stats[k].max)}`}
                  />
                </div>
              </section>
              <OverallInsight scores={data.averages} insights={insights} heading="Group overall" />
              <AreaInsights scores={data.averages} insights={insights} heading="What each petal means for this group" />
              <div className="mt-10 border-t border-slate-200 pt-6"><HeatLegend /></div>
              <QuarterlySection quarters={data.quarters} />
              <DemographicSection demographics={data.demographics} />
              <TeamSection entries={data.team_entries} />
              <section className="page-break mt-12" data-testid="group-addendum">
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Addendum · Individual heat maps</h2>
                <p className="mt-1 text-sm text-slate-500">{data.count} participant{data.count === 1 ? "" : "s"} included in this report (each person's newest result).</p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {data.members.map((m) => <MemberCard key={m.id} m={m} />)}
                </div>
              </section>
            </>
          )}
        </article>
      )}
    </div>
  );
}

const QuarterlySection = ({ quarters }) => {
  if (!quarters?.length) return null;
  const newestFirst = [...quarters].reverse();
  return (
    <section className="page-break mt-12" data-testid="group-quarterly">
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">Group progress by quarter</h2>
      <p className="mt-1 text-sm text-slate-500">
        The group's score as of each quarter end (March, June, September, December), using each person's newest result up to that date.
      </p>
      {quarters.length > 1 && (
        <div className="mt-6">
          <AreaTimeline points={quarters.map((q) => ({ label: q.label, scores: q.averages }))} testId="group-timeline" />
        </div>
      )}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {newestFirst.map((q) => (
          <div key={q.label} className="avoid-break rounded-xl border border-slate-200 bg-white p-3" data-testid={`quarter-card-${q.label.replace(" ", "-")}`}>
            <div className="flex justify-center"><FlowerHeatmap scores={q.averages} size={140} labels={false} testId={`quarter-heatmap-${q.label.replace(" ", "-")}`} /></div>
            <div className="mt-2 font-display font-semibold text-slate-900">{q.label}</div>
            <div className="text-[11px] text-slate-500">as of {fmtDate(q.end)} · {q.count} people</div>
            <div className="mt-2 grid grid-cols-5 gap-1 text-center">
              {AREAS.map((a) => (
                <div key={a.key}>
                  <div className="text-[9px] uppercase tracking-wide text-slate-400">{a.label.slice(0, 3)}</div>
                  <div className="font-mono text-xs text-slate-800">{fmtScore(q.averages[a.key])}</div>
                </div>
              ))}
            </div>
            <div className="mt-1.5 flex justify-between text-xs">
              <span style={{ color: band(average(q.averages)).hex }} className="font-semibold">{band(average(q.averages)).label}</span>
              <span className="font-mono font-semibold text-[#1F4266]">Total {fmtScore(total(q.averages))}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl bg-slate-50 p-4">
    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
    <div className="mt-1 font-display text-xl font-bold text-slate-900" data-testid={testId}>{value}</div>
  </div>
);
